/**
 * All Properties Page with API Integration
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  Card,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Chip,
  Avatar,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FilterListIcon from "@mui/icons-material/FilterList";
import AddIcon from "@mui/icons-material/Add";

import { propertyService } from "services/firestoreService";
import StandardPage from "components/StandardPage";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ResponsiveTable from "components/ResponsiveTable";

function AllProperties() {
  // Hooks
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  
  // State management
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [currentFilter, setCurrentFilter] = useState("All Properties");


  // Fetch properties from Firestore
  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      const fetchProperties = async () => {
        try {
          setLoading(true);
          setError(null);

          let data = await propertyService.getAll(user.uid);

          // Apply filter
          if (currentFilter !== "All Properties") {
            data = data.filter(p => p.property_type === currentFilter);
          }

          setProperties(data);
        } catch (error) {
          console.error('Error fetching properties:', error);
          setError(error.message);
        } finally {
          setLoading(false);
        }
      };
      
      fetchProperties();
    } else if (!authLoading && !isAuthenticated) {
      setLoading(false);
    }
  }, [currentFilter, isAuthenticated, authLoading]);

  // Menu handlers
  const handleMenuClick = (event, property) => {
    setAnchorEl(event.currentTarget);
    setSelectedProperty(property);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProperty(null);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (filterType = null) => {
    setFilterAnchorEl(null);
    if (filterType) {
      setCurrentFilter(filterType);
    }
  };

  // Menu action handlers
  const handleViewDetails = () => {
    if (selectedProperty) {
      navigate(`/properties/${selectedProperty.id}`);
    }
    handleMenuClose();
  };

  const handleEditProperty = () => {
    if (selectedProperty) {
      console.log('Edit property:', selectedProperty);
    }
    handleMenuClose();
  };

  const handleFinancialReport = () => {
    if (selectedProperty) {
      console.log('Generate financial report for:', selectedProperty);
    }
    handleMenuClose();
  };

  const handleArchive = () => {
    if (selectedProperty) {
      console.log('Archive property:', selectedProperty);
    }
    handleMenuClose();
  };

  const handleAddProperty = () => {
    navigate('/properties/add');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Under Contract":
        return "warning";
      case "Sold":
        return "info";
      default:
        return "default";
    }
  };

  // Prepare table data for ResponsiveTable
  const prepareTableData = () => {
    const columns = [
      { name: "property", align: "left" },
      { name: "type", align: "left" },
      { name: "value", align: "left" },
      { name: "roi", align: "left" },
      { name: "occupancy", align: "left" },
      { name: "status", align: "left" },
      { name: "actions", align: "center" }
    ];

    const rows = properties.map((property) => ({
      property: (
        <ArgonBox display="flex" alignItems="center">
          <ArgonBox mr={2}>
            <Avatar
              sx={{
                width: 48,
                height: 48,
                bgcolor: "rgba(200, 200, 200, 0.2)",
                color: "rgba(120, 120, 120, 1)",
                fontSize: "1.25rem",
                fontWeight: "bold",
              }}
            >
              {property.initial}
            </Avatar>
          </ArgonBox>
          <ArgonBox>
            <ArgonTypography variant="button" fontWeight="medium">
              {property.name}
            </ArgonTypography>
            <ArgonTypography variant="caption" color="text" display="block">
              {property.address}
            </ArgonTypography>
          </ArgonBox>
        </ArgonBox>
      ),
      type: (
        <ArgonTypography variant="caption" fontWeight="medium" textTransform="capitalize">
          {property.type}
        </ArgonTypography>
      ),
      value: (
        <ArgonTypography variant="button" fontWeight="medium">
          {property.value}
        </ArgonTypography>
      ),
      roi: (
        <ArgonTypography variant="button" fontWeight="medium" color="success">
          {property.roi}
        </ArgonTypography>
      ),
      occupancy: (
        <ArgonTypography variant="button" fontWeight="medium">
          {property.occupancy}
        </ArgonTypography>
      ),
      status: (
        <Chip
          label={property.status}
          color={getStatusColor(property.status)}
          size="small"
          sx={{ fontWeight: "medium" }}
        />
      ),
      actions: (
        <IconButton 
          aria-label="more" 
          onClick={(e) => handleMenuClick(e, property)} 
          size="small"
          sx={{ 
            minWidth: '44px',
            minHeight: '44px'
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }));

    return { columns, rows };
  };


  // Header actions for the StandardPage
  const headerActions = (
    <ArgonBox display="flex" gap={2}>
      <ArgonButton
        variant="outlined"
        color="info"
        size="small"
        startIcon={<FilterListIcon />}
        onClick={handleFilterClick}
        sx={{ height: '36px', fontSize: '0.875rem' }}
      >
        {currentFilter}
      </ArgonButton>
      <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={() => handleFilterClose()}>
        <MenuItem onClick={() => handleFilterClose("All Properties")}>All Properties</MenuItem>
        <MenuItem onClick={() => handleFilterClose("Commercial")}>Commercial</MenuItem>
        <MenuItem onClick={() => handleFilterClose("Residential")}>Residential</MenuItem>
        <MenuItem onClick={() => handleFilterClose("Retail")}>Retail</MenuItem>
        <MenuItem onClick={() => handleFilterClose("Industrial")}>Industrial</MenuItem>
        <MenuItem onClick={() => handleFilterClose("Mixed Use")}>Mixed Use</MenuItem>
      </Menu>
      <ArgonButton 
        variant="contained" 
        color="info" 
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAddProperty}
        sx={{ height: '36px', fontSize: '0.875rem' }}
      >
        Add Property
      </ArgonButton>
    </ArgonBox>
  );

  return (
    <StandardPage
      title="All Properties"
      subtitle={`Managing ${properties.length} investment properties${currentFilter !== "All Properties" ? ` (${currentFilter})` : ""}`}
      loading={loading || authLoading}
      error={error ? `Error loading properties: ${error}` : null}
      headerActions={headerActions}
      padding={2}
    >

        <Card>
          <ArgonBox p={2}>
            {properties.length === 0 ? (
              <ArgonBox textAlign="center" py={3}>
                <ArgonTypography variant="h6" color="text">
                  No properties found
                </ArgonTypography>
                <ArgonTypography variant="body2" color="text" mt={1}>
                  {currentFilter !== "All Properties" 
                    ? `No ${currentFilter.toLowerCase()} properties available.`
                    : "Start by adding your first property."
                  }
                </ArgonTypography>
              </ArgonBox>
            ) : (
              <ResponsiveTable 
                {...prepareTableData()}
                title={null}
                enableMobileCards={true}
              />
            )}
          </ArgonBox>
        </Card>

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewDetails}>View Details</MenuItem>
        <MenuItem onClick={handleEditProperty}>Edit Property</MenuItem>
        <MenuItem onClick={handleFinancialReport}>Financial Report</MenuItem>
        <MenuItem onClick={handleArchive}>Archive</MenuItem>
      </Menu>
    </StandardPage>
  );
}

export default AllProperties;